/**
 * services/EntryModelCategoryService.ts
 * Service for managing entry model categories
 *
 * Entry model categories organize different types of trade entry strategies
 * (e.g., "Aggressive", "Conservative", "Scale-In", "Breakout").
 *
 * Extends BaseService to provide CRUD operations with status-based filtering,
 * ordered retrieval, and drag-and-drop reordering support.
 */

import { BaseService } from './BaseService';
import type { EntryModelCategory } from '@/types/schema/EntryModelCategory';
import type { EntityStatus } from '@/types/shared/EntityStatus';
import { isEntityActive } from '@/types/shared/EntityStatus';
import { db } from '@/lib/db';

/**
 * Service for managing entry model categories
 *
 * Provides CRUD operations plus status-based filtering, ordered retrieval,
 * and reordering for drag-and-drop support.
 * Inherits getAll(), get(), update(), softDelete() from BaseService.
 */
export class EntryModelCategoryService extends BaseService<EntryModelCategory> {
  constructor() {
    super('entry_model_categories');
  }

  /**
   * Add a new entry model category with default status
   *
   * Defaults to status: 'active' if not provided.
   *
   * @param data - Partial category data
   * @returns Promise resolving to the created category
   */
  override async add(data: Partial<EntryModelCategory>): Promise<EntryModelCategory> {
    return super.add({ status: 'active', ...data });
  }

  /**
   * Get categories by status
   *
   * Filters categories by the specified EntityStatus.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @param status - The status to filter by ('active' | 'inactive' | 'archived')
   * @returns Promise resolving to array of categories with matching status
   */
  async getByStatus(status: EntityStatus): Promise<EntryModelCategory[]> {
    const allCategories = await this.getAll();
    return allCategories.filter(category => category.status === status);
  }

  /**
   * Get all active categories
   *
   * Convenience method for getting categories where status is 'active'.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @returns Promise resolving to array of active categories
   */
  async getActiveCategories(): Promise<EntryModelCategory[]> {
    const allCategories = await this.getAll();
    return allCategories.filter(category => isEntityActive(category.status));
  }

  /**
   * Get active categories sorted by display order
   *
   * Returns only active categories, sorted by `order` field ascending,
   * then by `createdAt` ascending for stable sorting when order values match.
   *
   * @returns Promise resolving to array of active categories in display order
   */
  async getOrderedCategories(): Promise<EntryModelCategory[]> {
    const activeCategories = await this.getActiveCategories();
    return activeCategories.sort((a, b) => {
      // Primary sort: order ascending
      if (a.order !== b.order) {
        return a.order - b.order;
      }
      // Secondary sort: createdAt ascending for stable sorting
      return a.createdAt.localeCompare(b.createdAt);
    });
  }

  /**
   * Reorder categories based on new position array
   *
   * Updates the `order` field for each category ID in the array,
   * using the array index as the new order value. Supports partial
   * reordering - IDs not in the array retain their current order.
   *
   * Operation is atomic - either all updates succeed or none do.
   * IDs not found in the database are silently skipped.
   *
   * @param ids - Array of category IDs in desired order
   */
  async reorder(ids: string[]): Promise<void> {
    await db.transaction('rw', [this.table, db.sync_operations, db.sync_state], async () => {
      for (let index = 0; index < ids.length; index++) {
        const id = ids[index];
        if (id === undefined) continue; // TypeScript strict mode guard
        try {
          await this.update(id, { order: index });
        } catch {
          // Silently skip IDs not found in database
          // BaseService.update throws if entity not found
        }
      }
    });
  }
}

// Export singleton instance
export const entryModelCategoryService = new EntryModelCategoryService();
